import { useCallback, useEffect, useState } from "react";
import {
  Location,
  Outlet,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { usePermissionValidator } from "../api/UserPermissionValidators";
import Button, {
  ButtonColorTypes,
  ButtonTypes,
} from "../shared-components/Button";
import PageHeader from "../shared-components/PageHeader";
import TabsSelection from "../shared-components/TabsSelection";

import useNavigateNewPerson from "../reports/useNavigateNewPerson";

enum Tabs360IdentifiersEnum {
  Queuing = "QUEUING",
  WebSearch = "WEB_SEARCH",
  Investigating = "INVESTIGATING",
  QA = "QA",
  Finished = "FINISHED",
  Amendment = "AMENDMENT",
}

/** When on ``/360/<tab>/v2025`` or ``.../v2026``, tab links keep that suffix; canonical ``/360/<tab>`` stays unsuffixed. */
function get360ListPathSuffix(pathname: string): "" | "/v2025" | "/v2026" {
  if (pathname.endsWith("/v2025")) return "/v2025";
  if (pathname.endsWith("/v2026")) return "/v2026";
  return "";
}

const ThreeSixty = () => {
  const { navigateCreatePerson } = useNavigateNewPerson();
  const navigate = useNavigate();
  const [, setParams] = useSearchParams();
  const location: Location = useLocation();
  const [initialTab, setInitialTab] = useState(Tabs360IdentifiersEnum.Finished);
  const {
    canView360,
    canViewWIP360Reports,
    canAddReport,
    canEditAmendment360,
  } = usePermissionValidator();

  const tabs: {
    identifier: string;
    icon?: any;
    label: string;
    href: string;
    noMinWidth?: boolean;
    fontSize?: string;
  }[] = [];

  const loadInitialDefaultTab = useCallback(() => {
    const suffix = get360ListPathSuffix(location.pathname);
    if (canViewWIP360Reports) {
      navigate(`/360/investigating${suffix}`);
    } else {
      navigate(`/360/finished${suffix}`);
    }
  }, [canViewWIP360Reports, navigate, location.pathname]);

  useEffect(() => {
    const listPaths: { base: string; tab: Tabs360IdentifiersEnum }[] = [
      { base: "/360/finished", tab: Tabs360IdentifiersEnum.Finished },
      { base: "/360/queuing", tab: Tabs360IdentifiersEnum.Queuing },
      { base: "/360/web_search", tab: Tabs360IdentifiersEnum.WebSearch },
      { base: "/360/investigating", tab: Tabs360IdentifiersEnum.Investigating },
      { base: "/360/qa", tab: Tabs360IdentifiersEnum.QA },
      { base: "/360/amendment", tab: Tabs360IdentifiersEnum.Amendment },
    ];
    const pathname = location.pathname;
    const matched = listPaths.find(
      ({ base }) =>
        pathname === base ||
        pathname === `${base}/v2025` ||
        pathname === `${base}/v2026`
    );
    if (matched) {
      setInitialTab(matched.tab);
    } else {
      loadInitialDefaultTab();
    }
  }, [location.pathname, loadInitialDefaultTab]);

  function fillTabs() {
    const listPathSuffix = get360ListPathSuffix(location.pathname);
    if (canViewWIP360Reports) {
      tabs.push({
        identifier: Tabs360IdentifiersEnum.Queuing,
        label: "Queuing",
        href: `/360/queuing${listPathSuffix}`,
        noMinWidth: true,
        fontSize: "text-lg",
      });
      tabs.push({
        identifier: Tabs360IdentifiersEnum.WebSearch,
        label: "Web Search",
        href: `/360/web_search${listPathSuffix}`,
        noMinWidth: true,
        fontSize: "text-lg",
      });
      tabs.push({
        identifier: Tabs360IdentifiersEnum.Investigating,
        label: "Investigating",
        href: `/360/investigating${listPathSuffix}`,
        noMinWidth: true,
        fontSize: "text-lg",
      });
      tabs.push({
        identifier: Tabs360IdentifiersEnum.QA,
        label: "360 QA",
        href: `/360/qa${listPathSuffix}`,
        noMinWidth: true,
        fontSize: "text-lg",
      });
    }
    if (canEditAmendment360) {
      tabs.push({
        identifier: Tabs360IdentifiersEnum.Amendment,
        label: "360 Amendment",
        href: `/360/amendment${listPathSuffix}`,
        noMinWidth: true,
        fontSize: "text-lg",
      });
    }

    if (canView360) {
      tabs.push({
        identifier: Tabs360IdentifiersEnum.Finished,
        label: "Finished",
        href: `/360/finished${listPathSuffix}`,
        noMinWidth: true,
        fontSize: "text-lg",
      });
    }
  }

  fillTabs();

  function handleTabSelection() {
    setParams({});
  }

  function CanAddReportButton() {
    if (!canAddReport) {
      return <></>;
    }
    return (
      <Button
        type={ButtonTypes.add}
        text="Add New Report"
        colorType={ButtonColorTypes.success}
        onClick={() => navigateCreatePerson()}
      />
    );
  }

  return (
    <>
      <PageHeader title={"360 Reports"}>
        <div className="flex">
          <CanAddReportButton />
        </div>
      </PageHeader>
      <TabsSelection
        tabs={tabs}
        initial={initialTab}
        onChange={handleTabSelection}
      ></TabsSelection>
      <div className="pt-4 relative">
        <Outlet></Outlet>
      </div>
    </>
  );
};

export default ThreeSixty;
